import './paginated-rolodex-table.css'

import { SearchOutlined } from '@ant-design/icons'
import { Pagination } from 'antd'
import Table, { ColumnsType, TableProps } from 'antd/es/table'
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface'
import { OntologyRolodexEntryType } from 'features/dynamicOntology'
import { usePaginatedQuery } from 'features/fde/helpers/usePaginatedQuery'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import { GeckoEntry, RolodexEntryType, RolodexHistogramFilter } from 'types'

import { useRowSelection } from './hooks/RowSelectionHooks'
import { createTableFilters } from './SharedTableComponents'
import { TextFilterDropdown } from './TableFilterDropdowns'

const DEFAULT_PAGINATION: TablePaginationConfig & {
  pageSize: number
  current: number
} = {
  pageSize: 10,
  current: 1,
  showSizeChanger: true,
  pageSizeOptions: [10, 20, 30, 50, 100],
}

/**
 * Right-aligned row that hosts the table's `<Pagination>` underneath the
 * `<Table>`. Mirrors the spacing of AntD's default in-table pagination so
 * consumers see no visual change versus the previous inline rendering.
 */
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 0;

  .ant-pagination {
    margin: 0;
  }

  .ant-pagination-total-text {
    display: flex;
    align-items: center;
    height: 2rem;
    margin-inline-end: 0.75rem;
  }
`

type _PaginatedRolodexTableProps = {
  rolodexQuery: RolodexEntryQueryBody
  sortKeys?: Record<string, { field?: string; key?: string; type?: string }>
  filterKeys?: Record<string, { field?: string; key?: string; type?: string }>
  initialSortingOptions?: ReadonlyArray<
    SorterResult<PaginatedRolodexTableRecordType>
  >
  groupByType: OntologyRolodexEntryType
  linkedTypes?: ReadonlyArray<OntologyRolodexEntryType | string>
  childLinks?: ReadonlyArray<OntologyRolodexEntryType | string>
  selectedRowKeys: ReadonlyArray<string>
  setSelectedRowKeys?: (keys: ReadonlyArray<string>) => void
  columnFilterValues: ReadonlyMap<string, string>
  setColumnFilterValues?: Dispatch<
    SetStateAction<Readonly<Record<string, string>>>
  >
  extraColumns?: ColumnsType<PaginatedRolodexTableRecordType>
  tableFooterComponent?: ReactNode
  /**
   * When set, each paginated Rolodex row (e.g. one libra-ticket) may be mapped to multiple
   * table rows after links are assembled. Pagination total and page size remain **ticket** (groupBy)
   * scoped — the table body may therefore show more data rows than `pageSize`. Row selection is
   * disabled because antd keys no longer align with Rolodex group-by IDs.
   */
  expandRecords?: (
    rows: ReadonlyArray<PaginatedRolodexTableRecordType>
  ) => ReadonlyArray<PaginatedRolodexTableRecordType>
  /** Table rowKey; defaults to `row.id`. Use with `expandRecords` for composite IDs. */
  tableRowKey?: (row: PaginatedRolodexTableRecordType) => string
}

export type PaginatedRolodexTableRecordType = {
  id: string
  entries: ReadonlyArray<GeckoEntry>
}

export type PaginatedRolodexTableProps = _PaginatedRolodexTableProps &
  Omit<
    TableProps<PaginatedRolodexTableRecordType>,
    'dataSource' | 'onChange' | 'isRowSelectionEnabled'
  >

/**
 * Generic paginated table to paging through Rolodex entries.
 * You provide a base query and a rowMapper to convert the entries to rows your 'columns'
 * can render.
 *
 * Optionally, you can provide:
 *   - sortKeys: a map of the 'key' from the columns to a Rolodex field, so that we can apply sorting which
 *     respects the pagination. This is sadly only applicable to the 'type' in your rolodexQuery and upward links.
 *   - initialSortingOptions: an array of SorterResult to set the initial sort order of the table. If providing this,
 *     you should also set defaultSortOrder on the column config to 'ascend' or 'descend' to ensure the table UI sorts the data
 *     and shows what's being sorted and it's not just the rolodex query that is sorted.
 *   - expandRecords / tableRowKey: expand each paginated Rolodex row into multiple rendered rows while keeping pagination on the Rolodex type.
 *
 * This works by querying all of the IDs matching your query, and then loading the entries from the IDs, with the 'links'
 * parameter set to pull back upward links. Once you run that, a second query runs to pull back downward-links by querying
 * based on 'tags'.
 *
 * It wraps an Ant.d table to show the content.
 *
 * @param props props
 * @returns commponent
 */
const RawPaginatedRolodexTable: FC<PaginatedRolodexTableProps> = (props) => {
  const {
    rolodexQuery,
    columns,
    sortKeys,
    initialSortingOptions,
    groupByType,
    linkedTypes,
    childLinks,
    selectedRowKeys,
    setSelectedRowKeys,
    filterKeys,
    columnFilterValues,
    setColumnFilterValues,
    extraColumns,
    expandRecords,
    tableRowKey,
    tableFooterComponent,
    ...tableProps
  } = props

  const [pagination, setPagination] = useState<TablePaginationConfig>(() => ({
    ...DEFAULT_PAGINATION,
    ...(props.pagination && typeof props.pagination === 'object'
      ? props.pagination
      : {}),
  }))

  const [sorts, setSorts] = useState<
    ReadonlyArray<SorterResult<PaginatedRolodexTableRecordType>>
  >(initialSortingOptions ?? [])

  const orderBy = useMemo(
    () =>
      sorts
        .map((sort) => {
          const keyEntry = find(
            Object.entries(sortKeys ?? {}),
            ([key]) => key === sort.columnKey
          )
          if (!keyEntry) {
            return null
          }
          const { field, key, type } = keyEntry[1]
          return {
            key,
            field,
            type: type ?? groupByType ?? undefined,
            descending: sort.order === 'descend',
          }
        }, [])
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [groupByType, sortKeys, sorts]
  )

  const tableFilters: ReadonlyArray<RolodexHistogramFilter> = useMemo(
    () => createTableFilters(columnFilterValues, filterKeys ?? {}, groupByType),
    [filterKeys, groupByType, columnFilterValues]
  )

  const { pageSize, current } = pagination
  const queryPagination = useMemo(
    () => ({
      pageSize: pageSize ?? DEFAULT_PAGINATION.pageSize,
      currentPage: current ?? DEFAULT_PAGINATION.current,
    }),
    [pageSize, current]
  )
  const {
    query: entryQuery,
    isLoading: queryLoading,
    totalEntries,
    sorter: groupBySorter,
  } = usePaginatedQuery(
    useMemo(
      () => ({
        ...rolodexQuery,
        links: uniq([
          ...(rolodexQuery.links ?? []),
          ...(linkedTypes ?? []).filter((t) => t !== rolodexQuery.type),
        ]) as RolodexEntryType[],
        filters: [
          ...((rolodexQuery.filters ?? []) as RolodexHistogramFilter[]),
          ...tableFilters,
        ],
        order_by: orderBy,
      }),
      [linkedTypes, orderBy, rolodexQuery, tableFilters]
    ),
    queryPagination,
    groupByType
  )
  const { entries, isLoading: entriesLoading } = useRolodexEntries(entryQuery)

  // If the number of entries changes (like, if we apply filters above), you
  // can end up on a page that doesn't exist - this enforces that you're on
  // a valid page, and handles updating the 'total' for the pagination object.
  useEffect(() => {
    setPagination((prev) => {
      const totalPages = Math.ceil(
        totalEntries / (pagination.pageSize ?? DEFAULT_PAGINATION.pageSize)
      )
      const current = isNil(prev.current)
        ? 1
        : prev.current > totalPages
          ? 1
          : prev.current
      // if nothing needs updating, return prev to avoid a re-render
      if (current === prev.current && totalEntries === prev.total) {
        return prev
      }
      return {
        ...prev,
        current,
        total: totalEntries,
      }
    })
  }, [pagination.pageSize, totalEntries])

  const linkedEntryQuery = useMemo(
    () =>
      childLinks?.length && entryQuery?.ids?.length
        ? {
            type: [...childLinks] as RolodexEntryType[],
            tags: {
              [groupByType]: entryQuery.ids,
            },
          }
        : [],
    [childLinks, entryQuery?.ids, groupByType]
  )

  // Ideally we wouldn't make this query if props.linkedEntryTypes is empty.
  const { entries: childLinked, isLoading: childrenLoading } =
    useRolodexEntries(linkedEntryQuery)

  // AntD's pagination is always disabled (see render below) because in
  // `expandRecords` mode the body row count would otherwise be sliced
  // against the server-scoped `total`. So we only read sort updates from
  // the table callback; pagination is driven by the standalone control.
  const tableOnChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<PaginatedRolodexTableRecordType>
        | SorterResult<PaginatedRolodexTableRecordType>[]
    ) => {
      setSorts(Array.isArray(sorter) ? sorter : [sorter])
    },
    [setSorts]
  )

  const handlePaginationChange = useCallback(
    (page: number, newPageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: newPageSize,
      }))
    },
    []
  )

  const filterValueSetter: (
    key: string
  ) => Dispatch<SetStateAction<string | undefined>> = useCallback(
    (key) => {
      return (val: SetStateAction<string | undefined>) => {
        setColumnFilterValues?.((prevFilters) => {
          const prev = prevFilters[key]
          const newVal = typeof val === 'function' ? val(prev) : val
          return newVal
            ? { ...prevFilters, [key]: newVal }
            : omit(prevFilters, key)
        })
      }
    },
    [setColumnFilterValues]
  )

  const safeColumns = useMemo<
    ColumnsType<PaginatedRolodexTableRecordType> | undefined
  >(
    () => [
      ...(columns?.map((c) => ({
        ...c,
        sorter: !!sortKeys?.[c.key as string],
        sortOrder: sorts.find((s) => s.columnKey === c.key)?.order,
        className: 'min-width-column',
        filterDropdown:
          setColumnFilterValues && filterKeys?.[c.key as string] ? (
            <TextFilterDropdown
              setFilterValue={filterValueSetter(c.key as string)}
              filterValue={columnFilterValues.get(c.key as string)}
            />
          ) : undefined,
        filterDropdownProps: { destroyPopupOnHide: true },
        filtered: !!columnFilterValues.get(c.key as string),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined
            style={{ color: filtered ? GeckoTheme.colors.highVis : undefined }}
          />
        ),
      })) ?? []),
      ...(extraColumns ?? []),
    ],
    [
      columns,
      extraColumns,
      sortKeys,
      sorts,
      setColumnFilterValues,
      filterKeys,
      filterValueSetter,
      columnFilterValues,
    ]
  )

  const allEntries = useMemo(
    () => [...entries, ...childLinked],
    [childLinked, entries]
  )

  const data = useMemo<ReadonlyArray<PaginatedRolodexTableRecordType>>(
    () =>
      entries
        // The 'entries' array is loaded by the 'ids' param, which means
        // that it ignores the orderBy - re-sort the page on load, which
        // is sad
        .toSorted(groupBySorter)
        .filter((e) => e.type === (groupByType ?? rolodexQuery.type))
        .map((entry) => {
          const linkedTo = entries.filter((e) => entry.tags[e.type] === e.id)
          const linkedFrom = allEntries.filter(
            (e) => e.tags[entry.type] === entry.id
          )
          return {
            id: entry.id,
            entries: [entry, ...linkedTo, ...linkedFrom],
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== undefined),
    [allEntries, entries, groupBySorter, groupByType, rolodexQuery.type]
  )

  const displayData = useMemo(() => {
    return expandRecords ? expandRecords(data) : data
  }, [data, expandRecords])

  const rowSelection = useRowSelection<PaginatedRolodexTableRecordType>(
    entries,
    groupByType,
    selectedRowKeys,
    expandRecords ? undefined : setSelectedRowKeys
  )

  const isLoading =
    entriesLoading || props.loading || queryLoading || childrenLoading

  const resolvedRowKey =
    tableRowKey ?? ((row: PaginatedRolodexTableRecordType) => row.id)

  // We disable AntD's internal table pagination and drive paging via the
  // external `<Pagination>` below. This avoids AntD's `pageData` memo
  // re-slicing `dataSource` against the server-scoped `total`, which would
  // empty later pages whenever `expandRecords` produced more body rows
  // than `pageSize`.
  return (
    <>
      <Table<PaginatedRolodexTableRecordType>
        {...tableProps}
        loading={isLoading}
        rowSelection={
          expandRecords || !setSelectedRowKeys ? undefined : rowSelection
        }
        rowKey={(row) => resolvedRowKey(row)}
        columns={safeColumns}
        onChange={tableOnChange}
        dataSource={[...displayData]}
        pagination={false}
        size='small'
        scroll={{ x: 'max-content', ...tableProps.scroll }}
      />
      <PaginationRow>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={totalEntries}
          showSizeChanger={pagination.showSizeChanger}
          pageSizeOptions={pagination.pageSizeOptions}
          showTotal={
            tableFooterComponent
              ? () => tableFooterComponent
              : pagination.showTotal
          }
          onChange={handlePaginationChange}
          disabled={!!isLoading}
        />
      </PaginationRow>
    </>
  )
}

export const PaginatedRolodexTable = React.memo(RawPaginatedRolodexTable)
